// @flow
// React Router v6 auth guard replacing the old PrivateRoute class component.
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import qs from 'query-string';

import {
    appLoad,
    appRedirect,
    appRedirected,
    appSetDispatchedDomain,
    appSetStrictOauth2,
    resolveStateBinding,
    userCannot,
} from '../redux/actions';
import cookieToken from '../helpers/cookieToken';
import Loading from '../pages/Loading';
import { allFlattenRoutes as routes } from './index';
import type { RouteType } from '../flow/RouteType';

type Props = {
    children: React.Node,
    path?: string,
};

const RequireAuth = ({ children, path: routePath }: Props) => {
    const dispatch = useDispatch();
    const app = useSelector((state: any) => state.App);
    const location = useLocation();
    const params = useParams();
    const hasBootstrapped = useRef(false);

    // Bootstrap: parse plugin/token/oauth2 query params and call appLoad once
    useEffect(() => {
        if (app.loading && !hasBootstrapped.current) {
            hasBootstrapped.current = true;

            // Flush the plugin token from cookie
            cookieToken.removePluginToken();

            // Check query params for plugin OAuth
            const { search } = location;
            const { plugin, token, oauth2 } = qs.parse(search);

            if (plugin) {
                let pluginDomainUrl = null;
                try {
                    pluginDomainUrl = new URL(plugin);
                } catch {
                    // Invalid URL, ignore
                }

                if (pluginDomainUrl) {
                    // Empty out the search and hash
                    pluginDomainUrl.search = '';
                    pluginDomainUrl.hash = '';
                    dispatch(appSetDispatchedDomain(pluginDomainUrl.href));
                    if (token) {
                        // Set the token to the cookie
                        cookieToken.setPluginToken(String(token));
                    }
                }

                const isStrictMode = oauth2 === 'strict';
                if (isStrictMode) {
                    dispatch(appSetStrictOauth2(true));
                }
            }

            // Load the async functions needed for the global app
            let domain_id = null;
            if (routePath && routePath.startsWith('/domain/:domain_id')) {
                // In case a domain is selected in the url we need to load it as we use it in the route
                domain_id = params.domain_id;
            }
            if (routePath && routePath.startsWith('/accounts/:account_id/subscriptions/:domain_id/checkout')) {
                // Checkout page
                domain_id = params.domain_id;
            }

            dispatch(appLoad(domain_id));
        }
    }, [app.loading, dispatch, location, routePath, params]);

    // Policy check — mirrors PrivateRoute.componentDidUpdate
    useEffect(() => {
        if (!app.loading && app.connected && routePath) {
            const currentRoute: ?RouteType = routes.find((r: RouteType) => r.path === routePath);

            if (currentRoute && currentRoute.policy) {
                const urlParamKeys = Object.keys(params);
                const firstUrlParamName = urlParamKeys[0];
                const firstUrlParamValue = firstUrlParamName ? parseInt(params[firstUrlParamName], 10) : null;
                const entity = firstUrlParamValue ? dispatch(resolveStateBinding(firstUrlParamName, firstUrlParamValue)) : null;

                const policies = Array.isArray(currentRoute.policy) ? currentRoute.policy : [currentRoute.policy];

                for (let i = 0; i < policies.length; i++) {
                    const policy = policies[i];
                    const allowed = dispatch(userCannot(policy, entity));
                    if (allowed === 'shopify-managed') {
                        console.warn('Access denied due to Shopify managed account', { path: routePath });
                        const firstDomain = app.domains[0];
                        if (firstDomain) {
                            dispatch(appRedirect(`/domain/${firstDomain.id}/dashboard`));
                        }
                        dispatch(appLoad());
                        return;
                    } else if (allowed) {
                        console.error('Access denied due to route policy', { path: routePath, index: i });
                        dispatch(appRedirect('/error-403'));
                        dispatch(appLoad());
                        return;
                    }
                }
            }
        }
    }, [app.loading, app.connected, app.domains, routePath, params, dispatch]);

    // Consume app.redirect to prevent loops
    useEffect(() => {
        if (app.redirect !== null) {
            dispatch(appRedirected());
        }
    }, [app.redirect, dispatch]);

    // Render logic — mirrors PrivateRoute.render
    const { search } = location;
    const { start } = qs.parse(search) || {};

    if (app.redirect !== null) {
        const redirectPath = String(app.redirect);
        const shouldPreserveCurrentSearch = !app.query && redirectPath === location.pathname;
        const queryStr = app.query
            ? (String(app.query).startsWith('?') ? app.query : `?${app.query}`)
            : (shouldPreserveCurrentSearch ? location.search : '');
        return <Navigate to={app.redirect + queryStr} replace />;
    }

    if (app.loading) {
        return <Loading />;
    }

    if (!app.connected) {
        // not logged in so redirect to login page with the return url
        const loginOrRegister = start === 'register' ? '/account/register' : '/account/login';
        return <Navigate to={loginOrRegister} state={{ from: location }} replace />;
    }

    // Do not open Language settings page for WP and Joomla
    if (
        app.domain &&
        app.domain.platform &&
        ['wordpress', 'joomla'].includes(app.domain.platform) &&
        routePath === '/domain/:domain_id/languages/settings'
    ) {
        return <Navigate to={`/domain/${app.domain.id}/dashboard`} replace />;
    }

    return children;
};

export default RequireAuth;
